var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
/* eslint-disable @typescript-eslint/naming-convention */
import React, { memo, useCallback, useContext, useMemo } from 'react';
import { emitPage, PageEventListenerTypes, generatePluginParams, onComponentEventAction, offComponentEventAction, emitCustomEvent, wrapHocOnce, getComponentId, } from '@tencent/gems-shared';
import { handleSchema, memoCompare } from '@tencent/gems-renderer-core';
import { StyleInjectHandler } from './handlers/StyleInjectHandler';
import { ComponentActionHandler } from './handlers/actionHandler';
import { PositionHandler } from './handlers/PositionHandler';
import { useRenderStore, useSchemaStore } from './store';
import { IteratorContext } from './IteratorContext';
import { HotAreas } from './HotAreas';
import { OptionContext } from './OptionContext';
if (typeof window === 'undefined') {
    // 兼容 ssr 时，window 为 undefined 导致报错的情况
    global.window = {};
}
var IComponentItem = function (props) {
    var _a;
    var meta = props.meta, global = props.global, instance = props.instance, path = props.path, injectProps = props.injectProps, hotAreas = props.hotAreas, children = props.children, withComponentAdaptor = props.withComponentAdaptor, store = props.store, eventHandlers = props.eventHandlers, restProps = __rest(props, ["meta", "global", "instance", "path", "injectProps", "hotAreas", "children", "withComponentAdaptor", "store", "eventHandlers"]);
    var key = instance.key, name = instance.name, _b = instance.component, Component = _b.component, __implementHotArea = _b.__implementHotArea, position = instance.position, commonStyle = instance.commonStyle, data = instance.data, slot = instance.slot, style = instance.style;
    var emit = useCallback(function (trigger, payload) { return emitCustomEvent(instance, trigger, global, meta, payload, eventHandlers); }, [instance, global, meta, eventHandlers]);
    var on = useCallback(function (eventName, callback) { return onComponentEventAction(instance, eventName, callback); }, [instance]);
    var off = useCallback(function (eventName, callback) { return offComponentEventAction(instance, eventName, callback); }, [instance]);
    var currentPath = path + "/" + name;
    var finalHotAreas = __implementHotArea
        ? hotAreas !== null && hotAreas !== void 0 ? hotAreas : React.createElement(HotAreas, { global: global, meta: meta, instance: instance })
        : null;
    var renderStore = useRenderStore();
    var getStoreActionsByKey = useCallback(function (key) { return renderStore.getStoreActionsByKey(key); }, [renderStore]);
    var getParentSchemaStores = useCallback(function (name) { return renderStore.getParentSchemaStores(instance, name); }, [
        renderStore,
        instance,
    ]);
    var compProps = __assign(__assign(__assign({}, restProps), injectProps), { 
        // TODO[V] 后续vue adaptor去掉这个属性
        injectProps: injectProps,
        // from props
        meta: meta,
        global: global, hotAreas: finalHotAreas, store: store,
        // from instance
        name: name,
        key: key,
        data: data,
        slot: slot,
        style: style,
        commonStyle: commonStyle, componentKey: key, emit: emit,
        on: on,
        off: off, path: currentPath, getStoreActionsByKey: getStoreActionsByKey,
        getParentSchemaStores: getParentSchemaStores });
    // TODO id 和 name 的兼容应该在更前置的流程更显式地处理
    // vision 旧协议里没有 name 字段，gems 新协议里没有 id 字段，这里做兼容
    var componentId = (_a = instance.id) !== null && _a !== void 0 ? _a : instance.name;
    var WrappedComponent = withComponentAdaptor
        ? wrapHocOnce("WrappedComponent_" + componentId, Component, withComponentAdaptor)
        : Component;
    var content = (React.createElement(WrappedComponent, __assign({}, compProps, { instance: instance }), children));
    var positionContent = position ? (React.createElement(PositionHandler, { positionStyle: position, instance: instance }, content)) : (content);
    return (React.createElement(StyleInjectHandler, { instance: instance, scope: getComponentId(instance.key) },
        React.createElement(ComponentActionHandler, { instance: instance, meta: meta, global: global }, positionContent)));
};
var ComponentItem = memo(IComponentItem, function (prevProps, nextProps) {
    var isEqual = memoCompare.compare(prevProps, nextProps, [
        /^instance$/,
        /^instance\.data(?:\..*)?$/,
        /^instance\.commonStyle(?:\..*)?$/,
        /^instance\.wrapperStyle(?:\..*)?$/,
        /^instance\.actions(?:\..*)?$/,
        /^store(?:\..*)?$/, // 匹配store以及store中的属性
    ]);
    return isEqual;
});
var useExpressionData = function (instance, global, parseExpression) {
    var rootStore = useRenderStore().rootStore;
    var iterator = useContext(IteratorContext);
    var key = instance.key, data = instance.data, actions = instance.actions;
    var schemaHandlerParams = {
        rootStore: rootStore,
        global: global,
        storeId: key,
        parseExpression: parseExpression,
        iterator: iterator,
    };
    // 拿到解析之后的data以及相关联的storeId
    var _a = handleSchema(data, schemaHandlerParams), newData = _a.data, relativeStoreIds = _a.storeIds;
    var newActions = actions.map(function (action) {
        var _a = handleSchema(action.data, schemaHandlerParams), newActionData = _a.data, actionRelativeIds = _a.storeIds;
        relativeStoreIds = relativeStoreIds.concat(actionRelativeIds);
        return __assign(__assign({}, action), { data: newActionData });
    });
    // 拿到组件的store的state以及action
    var schemaStore = useSchemaStore(relativeStoreIds, instance, iterator);
    var newInstance = __assign(__assign({}, instance), { data: newData, actions: newActions });
    return { instance: newInstance, schemaStore: schemaStore };
};
var listenPageCloseEvent = function (_a) {
    var global = _a.global, meta = _a.meta;
    var start = new Date().getTime();
    window.addEventListener('beforeunload', function () {
        var end = new Date().getTime();
        // 监听关闭事件并计算浏览时长
        emitPage(PageEventListenerTypes.CLOSE, {
            global: global,
            meta: meta,
            addition: {
                visitDuration: end - start,
            },
        });
    });
};
var emptyChildren = [];
var RenderComponent = function (props) {
    var instance = props.instance, renderHoc = props.renderHoc, meta = props.meta, path = props.path, global = props.global, parseExpression = props.parseExpression, withComponentAdaptor = props.withComponentAdaptor, disableStore = props.disableStore;
    var newInstance = instance;
    var schemaStore = null;
    var result = useExpressionData(instance, global, parseExpression);
    if (!disableStore) {
        newInstance = result.instance;
        schemaStore = result.schemaStore;
    }
    var WrappedComponentItem = renderHoc
        ? wrapHocOnce('WrappedComponentItem', ComponentItem, renderHoc)
        : ComponentItem;
    var itemProps = __assign(__assign({}, props), { instance: newInstance, store: schemaStore });
    var children = useMemo(function () {
        var children = instance.children, name = instance.name;
        var p = path + "/" + name;
        return (children === null || children === void 0 ? void 0 : children.length)
            ? children === null || children === void 0 ? void 0 : children.map(function (childInstance, childIndex) { return (React.createElement(RenderComponent, { path: p, meta: meta, global: global, index: childIndex, renderHoc: renderHoc, key: childInstance.key, instance: childInstance, slot: childInstance.slot, parseExpression: parseExpression, withComponentAdaptor: withComponentAdaptor, disableStore: disableStore })); })
            : emptyChildren;
    }, [disableStore, global, instance, meta, parseExpression, path, renderHoc, withComponentAdaptor]);
    return React.createElement(WrappedComponentItem, __assign({}, itemProps), children);
};
export function AppRender(props) {
    var componentInstances = props.componentInstances, pluginInstances = props.pluginInstances;
    var optionContext = useContext(OptionContext);
    var meta = optionContext.meta, global = optionContext.global;
    var execPlugins = React.useCallback(function () {
        pluginInstances.forEach(function (plugin) {
            var pluginFn = plugin.plugin.plugin;
            var params = generatePluginParams(plugin, global, meta);
            pluginFn(params);
        });
    }, []);
    React.useEffect(function () {
        execPlugins();
        emitPage(PageEventListenerTypes.INIT, { global: global, meta: meta });
        listenPageCloseEvent({ global: global, meta: meta });
    }, []);
    return (React.createElement(React.Fragment, null, componentInstances.map(function (instance, index) { return (React.createElement(RenderComponent, __assign({ path: "", index: index, key: instance.key, instance: instance }, optionContext))); })));
}
//# sourceMappingURL=AppRender.js.map